import { motion } from "framer-motion";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Sparkles } from "lucide-react";
import StarBackground from "../components/StarBackground";
import { useState } from "react";

const translations = {
  en: {
    backToMyWork: "Back to My Work",
    title: "The Owl Nebula",
    subtitle: "M97 / NGC 3587 - Planetary Nebula in Ursa Major",
    intro: "The Owl Nebula (also known as Messier 97, M97 or NGC 3587) is a planetary nebula approximately 2,030 light years away in the constellation Ursa Major. Estimated to be about 8,000 years old, it is approximately circular in cross-section with a faint internal structure.",
    formation: "Formation",
    formationDesc: "It was formed from the outflow of material from the stellar wind of the central star as it evolved along the asymptotic giant branch. The nebula is arranged in three concentric shells, with the outermost shell being about 20–30% larger than the inner shell.",
    appearance: "Owl-like Appearance",
    appearanceDesc: "The owl-like appearance of the nebula is the result of an inner shell that is not circularly symmetric, but instead forms a barrel-like structure aligned at an angle of 45° to the line of sight.",
    composition: "Composition & Structure",
    compositionDesc: "The nebula holds about 0.13 solar masses (M☉) of matter, including hydrogen, helium, nitrogen, oxygen, and sulfur; all with a density of less than 100 particles per cubic centimeter. Its outer radius is around 0.91 ly (0.28 pc) and it is expanding with velocities in the range of 27–39 km/s into the surrounding interstellar medium.",
    centralStar: "Central Star",
    centralStarDesc: "The 14th magnitude central star has passed the turning point in its evolution and is condensing to form a white dwarf. It has 55–60% of solar mass, is 41 to 148 times solar luminosity (L☉), and has an effective temperature of 123,000 K. The star has been successfully resolved by the Spitzer Space Telescope as a point source that does not show the infrared excess characteristic of a circumstellar disk."
  },
  no: {
    backToMyWork: "Tilbake til Mitt Arbeid",
    title: "Ugletåken",
    subtitle: "M97 / NGC 3587 - Planetarisk Tåke i Store Bjørn",
    intro: "Ugletåken (også kjent som Messier 97, M97 eller NGC 3587) er en planetarisk tåke omtrent 2 030 lysår unna i stjernebildet Store Bjørn. Den er estimert til å være omtrent 8 000 år gammel, og er tilnærmet sirkulær i tverrsnitt med en svak indre struktur.",
    formation: "Dannelse",
    formationDesc: "Den ble dannet fra utstrømming av materiale fra stjernevinden til den sentrale stjernen da den utviklet seg langs den asymptotiske kjempegrenen. Tåken er ordnet i tre konsentriske skall, der det ytterste skallet er omtrent 20–30% større enn det indre skallet.",
    appearance: "Ugleaktig Utseende",
    appearanceDesc: "Det ugleaktige utseendet til tåken er resultatet av et indre skall som ikke er sirkulært symmetrisk, men i stedet danner en tønneaktig struktur som er justert i en vinkel på 45° i forhold til synslinjen.",
    composition: "Sammensetning og Struktur",
    compositionDesc: "Tåken inneholder omtrent 0,13 solmasser (M☉) av materie, inkludert hydrogen, helium, nitrogen, oksygen og svovel; alle med en tetthet på mindre enn 100 partikler per kubikkcentimeter. Dens ytre radius er rundt 0,91 lysår (0,28 pc) og den utvider seg med hastigheter i området 27–39 km/s inn i det omkringliggende interstellare mediet.",
    centralStar: "Sentralstjernen",
    centralStarDesc: "Den 14. størrelsesklasse sentralstjernen har passert vendepunktet i sin utvikling og kondenserer for å danne en hvit dverg. Den har 55–60% av solmassen, er 41 til 148 ganger solluminositet (L☉), og har en effektiv temperatur på 123 000 K. Stjernen har blitt vellykket oppløst av Spitzer-romteleskopet som en punktkilde som ikke viser det infrarøde overskuddet som er karakteristisk for en cirkumstellær disk."
  },
  pl: {
    backToMyWork: "Powrót do Mojej Pracy",
    title: "Mgławica Sowa",
    subtitle: "M97 / NGC 3587 - Mgławica Planetarna w Wielkiej Niedźwiedzicy",
    intro: "Mgławica Sowa (znana również jako Messier 97, M97 lub NGC 3587) to mgławica planetarna oddalona o około 2 030 lat świetlnych w gwiazdozbiorze Wielkiej Niedźwiedzicy. Szacuje się, że ma około 8 000 lat i jest w przybliżeniu okrągła w przekroju ze słabą wewnętrzną strukturą.",
    formation: "Formowanie",
    formationDesc: "Powstała z wypływu materii z wiatru gwiazdowego centralnej gwiazdy, gdy ta ewoluowała wzdłuż asymptotycznej gałęzi olbrzymów. Mgławica jest ułożona w trzy koncentryczne powłoki, przy czym zewnętrzna powłoka jest o około 20–30% większa niż wewnętrzna.",
    appearance: "Sowi Wygląd",
    appearanceDesc: "Sowi wygląd mgławicy jest wynikiem wewnętrznej powłoki, która nie jest symetrycznie okrągła, ale zamiast tego tworzy strukturę beczkowatą ustawioną pod kątem 45° do linii wzroku.",
    composition: "Skład i Struktura",
    compositionDesc: "Mgławica zawiera około 0,13 mas Słońca (M☉) materii, w tym wodór, hel, azot, tlen i siarkę; wszystkie o gęstości mniejszej niż 100 cząstek na centymetr sześcienny. Jej zewnętrzny promień wynosi około 0,91 ly (0,28 pc) i rozszerza się z prędkościami w zakresie 27–39 km/s w otaczające środowisko międzygwiazdowe.",
    centralStar: "Gwiazda Centralna",
    centralStarDesc: "Centralna gwiazda o jasności 14 wielkości gwiazdowej przekroczyła punkt zwrotny w swojej ewolucji i kondensuje się, tworząc białego karła. Ma 55–60% masy Słońca, jest 41 do 148 razy jaśniejsza od Słońca (L☉) i ma efektywną temperaturę 123 000 K. Gwiazda została pomyślnie rozdzielona przez Kosmiczny Teleskop Spitzera jako źródło punktowe, które nie wykazuje nadmiaru podczerwieni charakterystycznego dla dysku okołogwiazdowego."
  }
};

export default function SnowballDetails() {
  const [searchParams] = useSearchParams();
  const [language, setLanguage] = useState(searchParams.get('lang') || 'en');
  const t = translations[language];

  return (
    <div className="min-h-screen">
      <StarBackground />
      
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-white/5 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-8 py-4 flex items-center justify-between">
          <Link to="/m31" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors duration-300">
            <ArrowLeft className="w-5 h-5" />
            <span>{t.backToMyWork}</span>
          </Link>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Sparkles className="text-purple-400 w-6 h-6" />
              <span className="text-xl font-bold" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                {t.title}
              </span>
            </div>
            
            {/* Language Selector */}
            <div className="flex items-center gap-2 ml-4 pl-4 border-l border-white/20">
              <button 
                className={`transition-colors duration-300 px-3 py-1 rounded-full ${
                  language === 'en' 
                    ? 'bg-white/20 text-white' 
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
                onClick={() => setLanguage('en')}
                data-testid="lang-en-btn"
              >
                EN
              </button>
              <button 
                className={`transition-colors duration-300 px-3 py-1 rounded-full ${
                  language === 'no' 
                    ? 'bg-white/20 text-white' 
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
                onClick={() => setLanguage('no')}
                data-testid="lang-no-btn"
              >
                NO
              </button>
              <button 
                className={`transition-colors duration-300 px-3 py-1 rounded-full ${
                  language === 'pl' 
                    ? 'bg-white/20 text-white' 
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
                onClick={() => setLanguage('pl')}
                data-testid="lang-pl-btn"
              >
                PL
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Content */}
      <section className="relative pt-32 pb-24 px-8" data-testid="snowball-details">
        <div className="max-w-4xl mx-auto">
          {/* Hero */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="mb-12"
          >
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-black mb-6 text-center"
              style={{ fontFamily: 'Unbounded, sans-serif' }}
              data-testid="snowball-title"
            >
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-400 via-indigo-400 to-blue-400">
                {t.title}
              </span>
            </h1>
            <p className="text-lg text-white/90 text-center mb-8">
              {t.subtitle}
            </p>
          </motion.div>

          {/* Content Card */}
          <motion.div 
            className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 md:p-12 shadow-[0_8px_32px_rgba(0,0,0,0.3)] space-y-8"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
          >
            {/* Overview */}
            <div>
              <p className="text-white/90 text-lg leading-relaxed mb-6">
                {t.intro}
              </p>
            </div>

            {/* Formation */}
            <div className="pt-6 border-t border-white/10">
              <h2 
                className="text-3xl font-bold mb-6"
                style={{ fontFamily: 'Unbounded, sans-serif' }}
              >
                {t.formation}
              </h2>
              <p className="text-white/90 text-lg leading-relaxed mb-6">
                {t.formationDesc}
              </p>
            </div>

            {/* Appearance */}
            <div className="pt-6 border-t border-white/10">
              <h2 
                className="text-3xl font-bold mb-6"
                style={{ fontFamily: 'Unbounded, sans-serif' }}
              >
                {t.appearance}
              </h2>
              <p className="text-white/90 text-lg leading-relaxed mb-6">
                {t.appearanceDesc}
              </p>
            </div>

            {/* Composition */}
            <div className="pt-6 border-t border-white/10">
              <h2 
                className="text-3xl font-bold mb-6"
                style={{ fontFamily: 'Unbounded, sans-serif' }}
              >
                {t.composition}
              </h2>
              <p className="text-white/90 text-lg leading-relaxed mb-6">
                {t.compositionDesc}
              </p>
            </div>

            {/* Central Star */}
            <div className="pt-6 border-t border-white/10">
              <div className="bg-purple-500/10 border border-purple-500/20 rounded-xl p-6">
                <h3 className="text-xl font-bold text-purple-300 mb-3" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                  {t.centralStar}
                </h3>
                <p className="text-white/80 leading-relaxed">
                  {t.centralStarDesc}
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
